import { useState, useEffect } from "react";
import { toast } from "sonner";
import { GraduationCap, X, Loader2, Sparkles, Code, Lightbulb, RefreshCw } from "lucide-react";
import { api } from "../lib/api";

const DEPTH_META = {
  beginner: { label: "Beginner", desc: "No jargon. Story-first.", tone: "bg-teal/15 text-teal" },
  builder: { label: "Builder", desc: "Practical. Tailwind + responsive.", tone: "bg-saffron/15 text-marigold" },
  pro: { label: "Pro", desc: "SSR, a11y, perf, SEO.", tone: "bg-ink/10 text-ink" },
};

export default function LearnWebsiteDialog({ open, onOpenChange, blueprint, initialSection = null }) {
  const [depth, setDepth] = useState("builder");
  const [section, setSection] = useState(initialSection);
  const [lessons, setLessons] = useState([]);
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState("overview");

  useEffect(() => {
    if (open && blueprint) fetchLessons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, depth, section]);

  const fetchLessons = async () => {
    setBusy(true);
    setLessons([]);
    try {
      const { data } = await api.post("/website/lessons", {
        blueprint,
        depth,
        section: section || undefined,
      });
      setLessons(data.lessons || []);
      setScope(data.scope || (section ? "section" : "overview"));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate lessons.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const homePage = (blueprint?.pages || [])[0] || {};
  const sections = homePage.sections || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-ink/60 backdrop-blur-sm animate-fade-in"
      onClick={() => onOpenChange(false)}
      data-testid="learn-website-dialog"
    >
      <div
        className="bg-cream rounded-3xl border-2 border-ink shadow-[10px_10px_0_0_#1A1B41] w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 md:p-6 border-b-2 border-border flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal/15 text-teal flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink2 font-semibold">Learn this website</div>
            <div className="font-heading font-semibold text-lg truncate">{blueprint?.title || "Website"}</div>
          </div>
          <button
            onClick={() => onOpenChange(false)}
            className="btn-ghost text-sm p-2"
            data-testid="learn-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Controls */}
        <div className="p-4 md:p-5 border-b border-border space-y-3 bg-paper/50">
          <div>
            <div className="text-xs uppercase tracking-wider text-ink2 font-semibold mb-2">Depth</div>
            <div className="flex gap-2" data-testid="learn-depth-toggle">
              {Object.keys(DEPTH_META).map(k => (
                <button
                  key={k}
                  onClick={() => setDepth(k)}
                  className={`px-4 py-2 rounded-full border-2 transition text-sm ${
                    depth === k
                      ? "border-ink bg-ink text-cream"
                      : "border-border bg-cream text-ink2 hover:border-ink"
                  }`}
                  data-testid={`learn-depth-${k}`}
                >
                  <span className="font-semibold">{DEPTH_META[k].label}</span>
                  <span className="hidden md:inline ml-2 opacity-70 text-xs">· {DEPTH_META[k].desc}</span>
                </button>
              ))}
            </div>
          </div>

          {sections.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-ink2 font-semibold mb-2">Scope</div>
              <div className="flex flex-wrap gap-1.5" data-testid="learn-scope-chips">
                <button
                  onClick={() => setSection(null)}
                  className={`text-xs px-3 py-1.5 rounded-full border transition ${
                    !section ? "bg-ink text-cream border-ink" : "bg-cream border-border hover:border-ink"
                  }`}
                  data-testid="learn-scope-overview"
                >
                  Whole home page
                </button>
                {sections.slice(0, 6).map((s, i) => (
                  <button
                    key={i}
                    onClick={() => setSection(s)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition ${
                      section === s ? "bg-ink text-cream border-ink" : "bg-cream border-border hover:border-ink"
                    }`}
                    data-testid={`learn-scope-section-${i}`}
                  >
                    {s.kind || `Section ${i + 1}`}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Lessons */}
        <div className="flex-1 overflow-y-auto p-5 md:p-6">
          {busy && (
            <div className="flex items-center justify-center py-16" data-testid="learn-busy">
              <div className="text-center">
                <Loader2 className="w-6 h-6 animate-spin text-marigold mx-auto mb-2" />
                <div className="text-sm text-ink2">Durga is preparing your lesson…</div>
              </div>
            </div>
          )}

          {!busy && lessons.length === 0 && (
            <div className="text-center py-16 text-ink2 text-sm">No lessons yet.</div>
          )}

          {!busy && lessons.length > 0 && (
            <ol className="space-y-4" data-testid="learn-lessons-list">
              {lessons.map((l, i) => (
                <li key={i} className="card-soft p-5" data-testid={`learn-lesson-${i}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-marigold text-cream flex items-center justify-center flex-shrink-0 font-heading font-bold text-sm">
                      {l.step || i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h3 className="font-heading font-semibold text-ink">{l.title}</h3>
                        {l.concept && (
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-paper border border-border text-ink2">
                            {l.concept}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-ink2 leading-relaxed mb-3">{l.explanation}</p>

                      {l.code_snippet && (
                        <div className="rounded-xl bg-codebg p-3 mb-3 overflow-x-auto">
                          <div className="flex items-center gap-1.5 text-cream/60 text-[10px] uppercase tracking-wider mb-1.5">
                            <Code className="w-3 h-3" /> Snippet
                          </div>
                          <pre className="text-xs text-cream font-mono whitespace-pre-wrap">{l.code_snippet}</pre>
                        </div>
                      )}

                      {l.try_this && (
                        <div className="rounded-xl bg-saffron/10 border border-saffron/30 p-3">
                          <div className="flex items-center gap-1.5 text-marigold text-[10px] uppercase tracking-wider mb-1 font-semibold">
                            <Lightbulb className="w-3 h-3" /> Try this
                          </div>
                          <div className="text-xs text-ink leading-relaxed">{l.try_this}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border flex items-center justify-between gap-2 bg-paper/30">
          <div className="text-xs text-ink2 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3 text-marigold" />
            {scope === "section" ? "Focused lesson" : "Home page walkthrough"} · {lessons.length} lesson{lessons.length === 1 ? "" : "s"}
          </div>
          <button onClick={fetchLessons} disabled={busy} className="btn-ghost text-xs" data-testid="learn-regenerate">
            <RefreshCw className="w-3 h-3" /> Regenerate
          </button>
        </div>
      </div>
    </div>
  );
}
